import { useRef, useState, type ReactNode } from 'react';
import { Typography, alpha, useTheme, Box } from '@mui/material';
import {
  DirectionsCar, LocalShipping, Handyman,
} from '@mui/icons-material';
import DispatchBookShell from './DispatchBookShell';
import DispatchRideBookWizard from './DispatchRideBookWizard';
import DispatchServiceBookWizard from './DispatchServiceBookWizard';
import { dispatchBookCopy, type DispatchBookMode } from './dispatchTypes';

export type BookMode = DispatchBookMode | null;

const MODE_META: Record<DispatchBookMode, { subtitle: string; icon: ReactNode; color: string }> = {
  ride: { subtitle: 'One page — customer, route, fare, and dispatch (same flow as mobile)', icon: <DirectionsCar />, color: '#2563eb' },
  porter: { subtitle: 'One page — customer, pickup/drop, vehicle, payment, and dispatch', icon: <LocalShipping />, color: '#0f766e' },
  on_demand: { subtitle: 'One page — customer, service, address, schedule, payment, and dispatch', icon: <Handyman />, color: '#0891b2' },
};

interface DispatcherBookDrawerProps {
  mode: BookMode;
  onClose: () => void;
  onSuccess: () => void;
}

export default function DispatcherBookDrawer({ mode, onClose, onSuccess }: DispatcherBookDrawerProps) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const submitRef = useRef<(() => Promise<void>) | null>(null);

  if (!mode) return null;

  const meta = MODE_META[mode];
  const copy = dispatchBookCopy(mode);

  const handleSubmit = () => submitRef.current?.();

  return (
    <DispatchBookShell
      open={!!mode}
      title={copy.newBooking}
      subtitle={meta.subtitle}
      accent={meta.color}
      icon={meta.icon}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={mode === 'ride' ? 'Book & Dispatch Ride' : 'Create & Dispatch'}
      loading={loading}
    >
      {mode === 'ride' ? (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <DispatchRideBookWizard
            key={mode}
            onSuccess={() => { onSuccess(); onClose(); }}
            onError={setError}
            setLoading={setLoading}
            setCanSubmit={() => undefined}
            registerSubmit={(fn) => { submitRef.current = fn; }}
          />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <DispatchServiceBookWizard
            key={mode}
            mode={mode}
            accent={meta.color}
            onSuccess={() => { onSuccess(); onClose(); }}
            onError={setError}
            setLoading={setLoading}
            setCanSubmit={() => undefined}
            registerSubmit={(fn) => { submitRef.current = fn; }}
          />
        </Box>
      )}

      {error && (
        <Typography variant="caption" color="error" fontWeight={700} sx={{ flexShrink: 0, mx: 0.5, mb: 0.5, p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.error.main, 0.08), border: `1px solid ${alpha(theme.palette.error.main, 0.16)}` }}>
          {error}
        </Typography>
      )}
    </DispatchBookShell>
  );
}
